import React, { useState } from 'react';
import HtmlEditor from './HtmlEditor';

interface MultiLangEditorProps {
    value: { en?: string; sv?: string; da?: string };
    onChange: (lang: string, html: string) => void;
    label: string;
    isDark?: boolean;
    rows?: number;
    useHtml?: boolean;
}

const LANGS = [
    { code: 'en', label: 'EN', flag: '🇬🇧' },
    { code: 'sv', label: 'SV', flag: '🇸🇪' },
    { code: 'da', label: 'DA', flag: '🇩🇰' },
];

const MultiLangEditor: React.FC<MultiLangEditorProps> = ({ value, onChange, label, isDark = true, rows = 4, useHtml = true }) => {
    const [activeLang, setActiveLang] = useState('en');

    const tabClass = (code: string) => {
        const isActive = activeLang === code;
        if (isDark) {
            return `px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${isActive ? 'bg-slate-700 text-white border-b-2 border-primary' : 'bg-slate-800/50 text-slate-400 hover:text-slate-200'}`;
        }
        return `px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${isActive ? 'bg-white text-slate-900 border-b-2 border-primary' : 'bg-slate-100 text-slate-500 hover:text-slate-700'}`;
    };

    const inputClass = `w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-slate-700 text-white border-slate-600 placeholder-slate-400' : 'bg-white text-slate-900 border-slate-300'} border`;

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{label}</label>
                <div className="flex gap-0.5">
                    {LANGS.map(lang => (
                        <button
                            key={lang.code}
                            type="button"
                            onClick={() => setActiveLang(lang.code)}
                            className={tabClass(lang.code)}
                        >
                            {lang.flag} {lang.label}
                        </button>
                    ))}
                </div>
            </div>
            {useHtml ? (
                <HtmlEditor
                    value={(value as any)?.[activeLang] || ''}
                    onChange={(html) => onChange(activeLang, html)}
                    placeholder={`${label} (${activeLang.toUpperCase()})`}
                    isDark={isDark}
                    rows={rows}
                />
            ) : (
                <textarea
                    value={(value as any)?.[activeLang] || ''}
                    onChange={(e) => onChange(activeLang, e.target.value)}
                    placeholder={`${label} (${activeLang.toUpperCase()})`}
                    className={inputClass}
                    rows={rows}
                />
            )}
            {/* Show which languages have content */}
            <div className="flex gap-2 mt-1">
                {LANGS.map(lang => {
                    const hasContent = !!(value as any)?.[lang.code];
                    return (
                        <span key={lang.code} className={`text-xs ${hasContent ? 'text-green-400' : isDark ? 'text-slate-600' : 'text-slate-300'}`}>
                            {lang.flag} {hasContent ? '✓' : '—'}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

export default MultiLangEditor;
